// src/pages/JournalPracticePage/index.js

import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Card,
  Button,
  Empty,
  Spin,
  Row,
  Col,
  Select,
  InputNumber,
  Table,
  Progress,
  Space,
  Descriptions,
  message,
} from 'antd';
import { ArrowLeftOutlined, FilePdfOutlined } from '@ant-design/icons';

import {
  getJournalPracticeList,
  getJournalExerciseById,
} from '../../services/journalService';
import { useAuth } from '../../context/AuthContext';

import { db } from '../../firebase-config';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';

const { Title, Text } = Typography;
const { Option } = Select;

/* =========================================================
   Hiệu ứng pháo hoa / confetti full-screen
   ========================================================= */
const FireworksOverlay = ({ visible, rockets = 24 }) => {
  if (!visible) return null;

  const bursts = Array.from({ length: rockets }).map((_, idx) => {
    const left = `${5 + Math.random() * 90}%`;
    const top = `${10 + Math.random() * 70}%`;
    const delay = `${Math.random() * 1.5}s`;
    const duration = `${1.8 + Math.random() * 0.7}s`;
    const size = 4 + Math.random() * 4;
    return { key: idx, left, top, delay, duration, size };
  });

  return (
    <>
      <style>
        {`
          .fireworks-overlay {
            position: fixed;
            inset: 0;
            pointer-events: none;
            z-index: 9999;
            overflow: hidden;
          }
          .fireworks-overlay .firework {
            position: absolute;
            border-radius: 50%;
            background: transparent;
            box-shadow:
              0 0 0 0 #ffffff,
              -10px -20px 0 0 #ff7675,
              20px -30px 0 0 #ffeaa7,
              -25px 10px 0 0 #55efc4,
              15px 25px 0 0 #74b9ff,
              -20px 30px 0 0 #a29bfe,
              5px -35px 0 0 #fd79a8,
              -30px -5px 0 0 #ffeaa7;
            animation-name: firework-burst;
            animation-timing-function: ease-out;
            animation-iteration-count: infinite;
          }
          @keyframes firework-burst {
            0% {
              transform: scale(0.2) translateY(20px);
              opacity: 1;
            }
            60% {
              transform: scale(1) translateY(-10px);
              opacity: 1;
            }
            100% {
              transform: scale(1.15) translateY(-20px);
              opacity: 0;
            }
          }
        `}
      </style>

      <div className="fireworks-overlay">
        {bursts.map((b) => (
          <div
            key={b.key}
            className="firework"
            style={{
              left: b.left,
              top: b.top,
              width: b.size,
              height: b.size,
              animationDuration: b.duration,
              animationDelay: b.delay,
            }}
          />
        ))}
      </div>
    </>
  );
};

/* =========================================================
   Chuẩn hoá 1 dòng bút toán
   ========================================================= */
const normalizeEntry = (e = {}) => {
  const toNumber = (val) => {
    if (val === null || val === undefined || val === '') return 0;
    const s = String(val).replace(/,/g, '');
    const n = Number(s);
    return Number.isNaN(n) ? 0 : n;
  };

  return {
    debitAccount: e.debitAccount || '',
    debitAmount: toNumber(e.debitAmount),
    creditAccount: e.creditAccount || '',
    creditAmount: toNumber(e.creditAmount),
  };
};

/* =========================================================
   So sánh đáp án: chỉ so tổng Nợ/Có theo từng tài khoản
   → cho phép đổi vị trí dòng, tách/gộp dòng vẫn đúng.
   ========================================================= */
const buildJournalMap = (entries = []) => {
  const map = {};
  entries.forEach((e) => {
    const { debitAccount, debitAmount, creditAccount, creditAmount } = e;
    if (debitAccount && debitAmount) {
      const key = `D|${debitAccount}`;
      map[key] = (map[key] || 0) + debitAmount;
    }
    if (creditAccount && creditAmount) {
      const key = `C|${creditAccount}`;
      map[key] = (map[key] || 0) + creditAmount;
    }
  });
  return map;
};

const mapsEqual = (a, b) => {
  const aKeys = Object.keys(a).filter((k) => a[k] !== 0);
  const bKeys = Object.keys(b).filter((k) => b[k] !== 0);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if ((a[k] || 0) !== (b[k] || 0)) return false;
  }
  return true;
};

const isJournalAnswerCorrect = (correctArr, userArr) => {
  if (!Array.isArray(correctArr) || !Array.isArray(userArr)) return false;
  const correctMap = buildJournalMap(correctArr);
  const userMap = buildJournalMap(userArr);
  return mapsEqual(correctMap, userMap);
};

/* =========================================================
   Form nhập 1 câu hỏi 仕訳
   ========================================================= */
const JournalQuestionForm = ({ question, onSubmit, disabled }) => {
  const answerLength = question.answer?.length || 1;
  const options = question.table?.options || [];

  const createEmptyRows = () =>
    Array.from({ length: answerLength }, () => ({
      debitAccount: null,
      debitAmount: null,
      creditAccount: null,
      creditAmount: null,
    }));

  const [rows, setRows] = useState(createEmptyRows);

  useEffect(() => {
    setRows(createEmptyRows());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [question.id]);

  const handleChange = (index, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleClick = () => {
    onSubmit(rows);
  };

  return (
    <div>
      {/* Nội dung câu hỏi */}
      {question.content?.parts?.map((p, idx) => (
        <p key={idx} style={{ fontSize: 16 }}>
          {p.value || p.text || ''}
        </p>
      ))}

      {/* Bảng nhập bút toán */}
      <Card size="small" style={{ marginTop: 16 }}>
        <Row gutter={8} style={{ fontWeight: 600, marginBottom: 8 }}>
          <Col span={6}>借方勘定科目</Col>
          <Col span={6}>借方金額</Col>
          <Col span={6}>貸方勘定科目</Col>
          <Col span={6}>貸方金額</Col>
        </Row>

        {rows.map((row, idx) => (
          <Row key={idx} gutter={8} style={{ marginBottom: 8 }}>
            <Col span={6}>
              <Select
                allowClear
                placeholder="借方科目"
                style={{ width: '100%' }}
                value={row.debitAccount}
                onChange={(val) => handleChange(idx, 'debitAccount', val)}
                disabled={disabled}
              >
                {options.map((op) => (
                  <Option key={op} value={op}>
                    {op}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="金額"
                min={0}
                value={row.debitAmount}
                onChange={(val) => handleChange(idx, 'debitAmount', val)}
                disabled={disabled}
                formatter={(value) =>
                  value != null
                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                parser={(value) => value.replace(/,/g, '')}
              />
            </Col>
            <Col span={6}>
              <Select
                allowClear
                placeholder="貸方科目"
                style={{ width: '100%' }}
                value={row.creditAccount}
                onChange={(val) => handleChange(idx, 'creditAccount', val)}
                disabled={disabled}
              >
                {options.map((op) => (
                  <Option key={op} value={op}>
                    {op}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col span={6}>
              <InputNumber
                style={{ width: '100%' }}
                placeholder="金額"
                min={0}
                value={row.creditAmount}
                onChange={(val) => handleChange(idx, 'creditAmount', val)}
                disabled={disabled}
                formatter={(value) =>
                  value != null
                    ? `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    : ''
                }
                parser={(value) => value.replace(/,/g, '')}
              />
            </Col>
          </Row>
        ))}
      </Card>

      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button
          type="primary"
          size="large"
          onClick={handleClick}
          disabled={disabled}
        >
          回答を記録して次の問題へ
        </Button>
      </div>
    </div>
  );
};

/* =========================================================
   Trang 仕訳練習 (ランダム)
   ========================================================= */
const JournalPracticePage = () => {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const [exercise, setExercise] = useState(null);
  const [loading, setLoading] = useState(true);

  // Lưu kết quả tất cả câu đã làm
  const [results, setResults] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  // Thông báo validate
  const [validationMessage, setValidationMessage] = useState(null);

  // Trạng thái lưu Firestore
  const [hasSavedResult, setHasSavedResult] = useState(false);
  const [saving, setSaving] = useState(false);

  // Toàn bộ danh sách R1-xx
  const journalExercises = useMemo(() => getJournalPracticeList(), []);
  const allIds = useMemo(
    () => journalExercises.map((ex) => ex.id),
    [journalExercises]
  );

  // Load câu hiện tại
  useEffect(() => {
    setLoading(true);
    const data = getJournalExerciseById(exerciseId);
    setExercise(data);
    setValidationMessage(null);
    setLoading(false);
  }, [exerciseId]);

  const handleBackToList = () => {
    navigate('/journal-practice');
  };

  const handleExportPDF = () => {
    // In ra PDF / A4 (sử dụng dialog in của trình duyệt)
    window.print();
  };

  /* ---------- Lưu kết quả vào Firestore sau khi hoàn thành ---------- */
  useEffect(() => {
    if (!showSummary || hasSavedResult || !results.length) return;

    const total = allIds.length;
    const correct = results.filter((r) => r.isCorrect).length;
    const accuracy = total ? Math.round((correct / total) * 100) : 0;

    // userId phải trùng với MyPage (currentUser.uid || currentUser.id)
    const userIdForResult =
      currentUser?.uid || currentUser?.id || null;

    if (!userIdForResult) {
      console.warn(
        '[journalPracticeResults] userId is null – vẫn lưu nhưng MyPage sẽ không filter được.'
      );
    }

    const saveResult = async () => {
      try {
        setSaving(true);

        await addDoc(collection(db, 'journalPracticeResults'), {
          userId: userIdForResult,
          email: currentUser?.email || null,
          fullName:
            currentUser?.fullName ||
            currentUser?.nameRomaji ||
            currentUser?.displayName ||
            currentUser?.email ||
            null,
          className: currentUser?.className || null,
          regNumber: currentUser?.regNumber || null,
          totalQuestions: total,
          correctAnswers: correct,
          accuracy,
          createdAt: serverTimestamp(),
          details: results,
        });

        setHasSavedResult(true);
        message.success('仕訳練習の結果を保存しました。');
      } catch (err) {
        console.error('Error saving journal result:', err);
        message.error('結果の保存に失敗しました。');
      } finally {
        setSaving(false);
      }
    };

    saveResult();
  }, [showSummary, hasSavedResult, results, allIds, currentUser]);

  /* ---------- Xử lý khi bấm "回答を記録して次の問題へ" ---------- */
  const handleSubmitAnswer = (userRowsRaw) => {
    if (!exercise || !exercise.questions || !exercise.questions[0]) return;

    // 1) kiểm tra có nhập gì chưa (mọi ô đều trống thì không cho qua)
    const anyFilled = userRowsRaw.some(
      (r) =>
        r.debitAccount ||
        r.creditAccount ||
        r.debitAmount != null ||
        r.creditAmount != null
    );
    if (!anyFilled) {
      setValidationMessage(
        '問題に回答してから次へ進んでください。（Hãy nhập câu trả lời trước khi sang câu tiếp theo）'
      );
      return;
    }

    // 2) CHỈ yêu cầu dòng đầu đủ 4 ô Nợ – Có
    const firstRow = userRowsRaw[0] || {};
    const hasFullFirstRow =
      firstRow.debitAccount &&
      firstRow.creditAccount &&
      firstRow.debitAmount != null &&
      firstRow.creditAmount != null;

    if (!hasFullFirstRow) {
      setValidationMessage(
        '1行目の借方・貸方の勘定科目と金額をすべて入力してください。（Chỉ cần điền đầy đủ hàng số 1 bên Nợ và Có）'
      );
      return;
    }

    setValidationMessage(null);

    const question = exercise.questions[0];

    // Chuẩn hoá & bỏ dòng trống
    const correctAnswer = (question.answer || [])
      .map(normalizeEntry)
      .filter(
        (r) =>
          r.debitAccount ||
          r.creditAccount ||
          r.debitAmount !== 0 ||
          r.creditAmount !== 0
      );

    const userAnswer = (userRowsRaw || [])
      .map(normalizeEntry)
      .filter(
        (r) =>
          r.debitAccount ||
          r.creditAccount ||
          r.debitAmount !== 0 ||
          r.creditAmount !== 0
      );

    // ✅ so sánh tổng Nợ/Có theo từng tài khoản
    const isCorrect = isJournalAnswerCorrect(correctAnswer, userAnswer);

    const questionText = (question.content?.parts || [])
      .map((p) => p.value || p.text || '')
      .join('\n');

    const updatedResults = (() => {
      const others = results.filter((r) => r.exerciseId !== exerciseId);
      return [
        ...others,
        {
          exerciseId,
          isCorrect,
          questionText,
          correctAnswer,
          userAnswer,
        },
      ];
    })();

    setResults(updatedResults);

    // Chọn câu tiếp theo (random) hoặc kết thúc
    const answeredIds = updatedResults.map((r) => r.exerciseId);
    const remainingIds = allIds.filter((id) => !answeredIds.includes(id));

    if (remainingIds.length === 0) {
      setShowSummary(true);
    } else {
      const nextId =
        remainingIds[Math.floor(Math.random() * remainingIds.length)];
      navigate(`/journal-practice/${nextId}`);
    }
  };

  /* ================== LOADING ================== */
  if (loading) {
    return <Spin size="large" style={{ display: 'block', marginTop: 50 }} />;
  }

  /* ================== TRANG THỐNG KÊ (SAU KHI LÀM XONG) ================== */
  if (showSummary) {
    const total = allIds.length;
    const correct = results.filter((r) => r.isCorrect).length;
    const incorrect = total - correct;
    const accuracy = total ? Math.round((correct / total) * 100) : 0;

    // Chỉ lấy những câu SAI để in chi tiết
    const incorrectOnly = results.filter((r) => !r.isCorrect);
    const sortedResults = incorrectOnly
      .slice()
      .sort((a, b) => a.exerciseId.localeCompare(b.exerciseId));

    const nowStr = new Date().toLocaleString('ja-JP');

    // Format tiền: nếu 0 → chuỗi rỗng (để PDF không hiện "0")
    const formatAmount = (v) => {
      if (v === null || v === undefined || v === 0) return '';
      try {
        return Number(v).toLocaleString('ja-JP');
      } catch {
        return '';
      }
    };

    const columnsJournal = [
      {
        title: '借方勘定科目',
        dataIndex: 'debitAccount',
        key: 'debitAccount',
      },
      {
        title: '借方金額',
        dataIndex: 'debitAmount',
        key: 'debitAmount',
        render: formatAmount,
      },
      {
        title: '貸方勘定科目',
        dataIndex: 'creditAccount',
        key: 'creditAccount',
      },
      {
        title: '貸方金額',
        dataIndex: 'creditAmount',
        key: 'creditAmount',
        render: formatAmount,
      },
    ];

    const photoUrl =
      currentUser?.avatar ||
      currentUser?.photoURL ||
      'https://via.placeholder.com/240x360?text=4x6+Photo';

    // 70% trở lên thì ăn mừng, càng cao càng nhiều pháo
    const shouldCelebrate = accuracy >= 70;
    let rockets = 16;
    if (accuracy >= 100) rockets = 32;
    else if (accuracy >= 90) rockets = 26;
    else if (accuracy >= 80) rockets = 22;

    return (
      <div style={{ padding: 16 }}>
        <FireworksOverlay visible={shouldCelebrate} rockets={rockets} />

        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBackToList}
          style={{ marginBottom: 16 }}
        >
          リストに戻る
        </Button>

        <Card
          style={{
            padding: 24,
            boxShadow: '0 0 4px rgba(0,0,0,0.15)',
            minHeight: '80vh',
          }}
        >
          <Title level={2} style={{ textAlign: 'center' }}>
            仕訳練習 結果レポート（R1-01〜R1-30）
          </Title>

          <Row gutter={16} style={{ marginTop: 24 }}>
            <Col xs={24} md={14}>
              {/* Bảng thông tin học sinh */}
              <Descriptions
                size="small"
                column={1}
                bordered
                labelStyle={{ width: 140 }}
              >
                <Descriptions.Item label="受験者">
                  {currentUser?.fullName ||
                    currentUser?.nameRomaji ||
                    currentUser?.displayName ||
                    currentUser?.email ||
                    '名無し'}
                </Descriptions.Item>
                <Descriptions.Item label="クラス・学籍番号">
                  {currentUser?.className && currentUser?.regNumber
                    ? `${currentUser.className}-${currentUser.regNumber}`
                    : currentUser?.className || '―'}
                </Descriptions.Item>
                <Descriptions.Item label="実施日時">
                  {nowStr}
                </Descriptions.Item>
                <Descriptions.Item label="総問題数">
                  {total}
                </Descriptions.Item>
                <Descriptions.Item label="正解数">
                  {correct}
                </Descriptions.Item>
                <Descriptions.Item label="不正解数">
                  {incorrect}
                </Descriptions.Item>
                <Descriptions.Item label="正解率">
                  {accuracy}%
                </Descriptions.Item>
              </Descriptions>
            </Col>

            <Col xs={24} md={5} style={{ textAlign: 'center', marginTop: 16 }}>
              <Progress
                type="circle"
                percent={accuracy}
                width={150}
                format={(p) => `${p}% 正解`}
              />
              {saving && (
                <div style={{ marginTop: 8 }}>
                  <Text type="secondary">保存中...</Text>
                </div>
              )}
            </Col>

            {/* Ảnh 4x6 của người làm */}
            <Col xs={24} md={5} style={{ marginTop: 16 }}>
              <div
                style={{
                  width: 160,
                  height: 240,
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  overflow: 'hidden',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              >
                <img
                  src={photoUrl}
                  alt="受験者写真"
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />
              </div>
              <div style={{ textAlign: 'center', marginTop: 8 }}>
                <Text type="secondary">受験者写真（4×6）</Text>
              </div>
            </Col>
          </Row>

          {/* Chỉ hiển thị CHI TIẾT CÂU SAI */}
          <Title level={4} style={{ marginTop: 32 }}>
            間違えた問題の詳細
          </Title>

          {sortedResults.length === 0 ? (
            <Card
              style={{
                marginTop: 16,
                background: '#f6ffed',
                borderColor: '#b7eb8f',
              }}
            >
              <Text strong>
                全ての問題に正解しました！素晴らしいです。（Không có câu sai nào）
              </Text>
            </Card>
          ) : (
            sortedResults.map((r) => (
              <Card
                key={r.exerciseId}
                size="small"
                style={{ marginTop: 16, pageBreakInside: 'avoid' }}
              >
                <Text strong>
                  問題ID：{r.exerciseId} （× 不正解）
                </Text>
                <div style={{ marginTop: 8, marginBottom: 8 }}>
                  <Text>{r.questionText}</Text>
                </div>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Card title="模範解答" size="small">
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={r.correctAnswer.map((row, idx) => ({
                          key: idx,
                          ...row,
                        }))}
                        columns={columnsJournal}
                      />
                    </Card>
                  </Col>
                  <Col xs={24} md={12}>
                    <Card title="あなたの回答" size="small">
                      <Table
                        size="small"
                        pagination={false}
                        dataSource={r.userAnswer.map((row, idx) => ({
                          key: idx,
                          ...row,
                        }))}
                        columns={columnsJournal}
                      />
                    </Card>
                  </Col>
                </Row>
              </Card>
            ))
          )}

          <div
            style={{
              marginTop: 32,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              rowGap: 8,
            }}
          >
            <Space>
              <Button onClick={handleBackToList}>もう一度ランダムに練習する</Button>
            </Space>

            <Space>
              <Button
                type="primary"
                icon={<FilePdfOutlined />}
                onClick={handleExportPDF}
              >
                PDF出力（印刷）
              </Button>
            </Space>
          </div>
        </Card>
      </div>
    );
  }

  /* ================== TRANG LÀM BÀI ================== */
  if (!exercise || !exercise.questions || !exercise.questions[0]) {
    return (
      <Empty description="仕訳練習の問題が見つかりません。(Không tìm thấy câu hỏi 仕訳練習)" />
    );
  }

  const question = exercise.questions[0];
  const answeredCount = results.length;
  const totalCount = allIds.length;

  return (
    <div style={{ padding: 16 }}>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={handleBackToList}
        style={{ marginBottom: 16 }}
      >
        戻る
      </Button>

      <Title level={2}>仕訳練習（R1 ランダム） - {exerciseId}</Title>

      <Text type="secondary">
        現在：{answeredCount + 1}問目／全{totalCount}問（答え合わせは最後にまとめて表示されます）
      </Text>

      <Card style={{ marginTop: 16 }}>
        <JournalQuestionForm
          question={question}
          onSubmit={handleSubmitAnswer}
          disabled={false}
        />
      </Card>

      {validationMessage && (
        <Card
          style={{
            marginTop: 16,
            borderColor: '#faad14',
            background: '#fffbe6',
          }}
        >
          <Text type="warning">{validationMessage}</Text>
        </Card>
      )}
    </div>
  );
};

export default JournalPracticePage;
